import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../src/lib/supabaseClient';
import { api } from '../services/apiClient';
import type { AuthLang } from './AuthModal';
import {
  AUTH_LOGIN_CAROUSEL_INTERVAL_MS,
  AUTH_LOGIN_CAROUSEL_SRC,
} from '../utils/authLoginCarousel';

interface LoginProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  language?: AuthLang;
  onSwitchToSignUp?: () => void;
  /** 打开「忘记密码」视图（由 AuthModal 等父级切换面板） */
  onForgotPassword?: () => void;
  isDarkMode?: boolean;
}

type LoginLang = AuthLang;

const LOGIN_LANGS: LoginLang[] = ['en', 'zh'];

const LOGIN_CAROUSEL_INTERVAL_MS = AUTH_LOGIN_CAROUSEL_INTERVAL_MS;
const LOGIN_CAROUSEL_SRC = AUTH_LOGIN_CAROUSEL_SRC;

const loginCopy: Record<
  LoginLang,
  {
    formTitle: string;
    descBeforeCode: string;
    descAfterCode: string;
    labelEmail: string;
    labelPassword: string;
    labelCode: string;
    phEmail: string;
    phPassword: string;
    phCode: string;
    hidePwd: string;
    showPwd: string;
    btnSendCode: string;
    btnVerify: string;
    btnSending: string;
    btnVerifying: string;
    footerHint: string;
    signUpCta: string;
    enterEmailPwd: string;
    enterCode: string;
    codeExpired: string;
    codeSentOk: string;
    loginOk: string;
    loginFail: string;
    emailNotConfirmed: string;
    sendCodeFail: (e: string) => string;
    resendIn: (s: number) => string;
    expiresIn: (m: number, s: number) => string;
    forgotPassword: string;
  }
> = {
  en: {
    formTitle: 'Sign in',
    descBeforeCode: 'Enter your email and password, then send the verification code to your inbox.',
    descAfterCode: 'Enter the 6-digit code from your email to complete sign-in.',
    labelEmail: 'EMAIL',
    labelPassword: 'PASSWORD',
    labelCode: 'VERIFICATION CODE',
    phEmail: 'name@domain.com',
    phPassword: '••••••••',
    phCode: '6-digit code',
    hidePwd: 'Hide password',
    showPwd: 'Show password',
    btnSendCode: 'Send code & sign in',
    btnVerify: 'Verify & sign in',
    btnSending: 'Sending…',
    btnVerifying: 'Verifying…',
    footerHint: 'New here? Use Register to create an account, then sign in with email code.',
    signUpCta: 'Register',
    enterEmailPwd: 'Please enter email and password first.',
    enterCode: 'Please enter the verification code.',
    codeExpired: 'The code has expired. Request a new one.',
    codeSentOk: 'Verification code sent. Check your email (valid 10 min).',
    loginOk: 'Signed in successfully.',
    loginFail: 'Sign-in failed.',
    emailNotConfirmed:
      'Your email is not confirmed. Check your inbox and use the confirmation link before signing in.',
    sendCodeFail: (e) => `Failed to send code: ${e}`,
    resendIn: (s) => `Resend in ${s}s`,
    expiresIn: (m, sec) => `Code expires in ${m}:${String(sec).padStart(2, '0')}`,
    forgotPassword: 'Forgot password?',
  },
  zh: {
    formTitle: '登录',
    descBeforeCode: '输入邮箱与密码，发送验证码到邮箱后继续。',
    descAfterCode: '请输入邮件中的 6 位验证码完成登录。',
    labelEmail: '邮箱',
    labelPassword: '密码',
    labelCode: '验证码',
    phEmail: 'name@domain.com',
    phPassword: '••••••••',
    phCode: '6 位验证码',
    hidePwd: '隐藏密码',
    showPwd: '显示密码',
    btnSendCode: '发送验证码并登录',
    btnVerify: '验证并登录',
    btnSending: '发送中…',
    btnVerifying: '验证中…',
    footerHint: '还没有账户？前往注册创建账户后，再使用邮箱验证码登录。',
    signUpCta: '立即注册',
    enterEmailPwd: '请先输入邮箱和密码。',
    enterCode: '请输入验证码。',
    codeExpired: '验证码已过期，请重新获取。',
    codeSentOk: '验证码已发送到您的邮箱，10 分钟内有效。',
    loginOk: '登录成功！',
    loginFail: '登录失败。',
    emailNotConfirmed: '您的邮箱未确认。请先查收邮件并点击确认链接后再登录。',
    sendCodeFail: (e) => `发送验证码失败：${e}`,
    resendIn: (s) => `${s} 秒后可重新发送`,
    expiresIn: (m, sec) => `验证码剩余：${m} 分 ${sec} 秒`,
    forgotPassword: '忘记密码？',
  },
};

const Login: React.FC<LoginProps> = ({
  onSuccess,
  onError,
  language = 'en',
  onSwitchToSignUp,
  onForgotPassword,
  isDarkMode = true,
}) => {
  const lang: LoginLang = LOGIN_LANGS.includes(language as LoginLang) ? (language as LoginLang) : 'en';
  const t = loginCopy[lang];
  const cjkHero = lang === 'zh';

  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    if (LOGIN_CAROUSEL_SRC.length <= 1) return;
    const id = window.setInterval(() => {
      setCarouselIdx((i) => (i + 1) % LOGIN_CAROUSEL_SRC.length);
    }, LOGIN_CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState<'success' | 'error'>('error');
  const [showPassword, setShowPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [codeSentTime, setCodeSentTime] = useState<number | null>(null);
  const [codeExpiresIn, setCodeExpiresIn] = useState<number | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiresTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionClearedRef = useRef<boolean>(false);

  const showMsg = (text: string, tone: 'success' | 'error') => {
    setMessage(text);
    setMessageTone(tone);
  };

  useEffect(() => {
    if (sessionClearedRef.current) {
      return;
    }

    const clearSessionTimer = setTimeout(async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) {
          sessionClearedRef.current = true;
          await supabase.auth.signOut();
        } else {
          sessionClearedRef.current = true;
        }
      } catch (error) {
        console.warn('⚠️ [登录] 清除session时出错:', error);
        sessionClearedRef.current = true;
      }
    }, 100);

    return () => {
      clearTimeout(clearSessionTimer);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (expiresTimerRef.current) clearInterval(expiresTimerRef.current);
    };
  }, []);

  const handleSendCode = async () => {
    if (!email || !password) {
      showMsg(t.enterEmailPwd, 'error');
      return;
    }

    setSendingCode(true);
    showMsg('', 'error');

    try {
      const result = await api.sendVerificationCode(email, password);

      setCodeSent(true);
      setCodeSentTime(Date.now());
      showMsg(t.codeSentOk, 'success');

      if (result.code && (import.meta.env.DEV || process.env.NODE_ENV === 'development')) {
        const sixDigitCode = String(result.code).replace(/\D/g, '').substring(0, 6);
        if (sixDigitCode) {
          // eslint-disable-next-line no-console
          console.log('[Dev] Verification code:', sixDigitCode);
        }
      }

      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      if (expiresTimerRef.current) clearInterval(expiresTimerRef.current);

      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            countdownTimerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      countdownTimerRef.current = timer;

      setCodeExpiresIn(10 * 60);
      const expiresTimerInstance = setInterval(() => {
        setCodeExpiresIn((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(expiresTimerInstance);
            expiresTimerRef.current = null;
            setCodeSent(false);
            setCodeSentTime(null);
            setVerificationCode('');
            showMsg(t.codeExpired, 'error');
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      expiresTimerRef.current = expiresTimerInstance;
    } catch (error: any) {
      const errorMsg = error.message || t.sendCodeFail('');
      showMsg(t.sendCodeFail(errorMsg), 'error');
      if (onError) onError(errorMsg);
    } finally {
      setSendingCode(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!codeSent) {
      await handleSendCode();
      return;
    }

    if (!verificationCode) {
      showMsg(t.enterCode, 'error');
      return;
    }

    if (codeSentTime && Date.now() - codeSentTime > 10 * 60 * 1000) {
      showMsg(t.codeExpired, 'error');
      setCodeSent(false);
      setCodeSentTime(null);
      setVerificationCode('');
      return;
    }

    setLoading(true);
    showMsg('', 'error');

    try {
      const result = await api.verifyCode(email, verificationCode, password);

      if (result.success) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          const isEmailConfirmed =
            data.user.email_confirmed_at != null ||
            (data.user as any).confirmed_at != null ||
            (data.user as any).email_verified === true;

          if (!isEmailConfirmed) {
            try {
              Object.keys(localStorage).forEach((key) => {
                if (key.startsWith('sb-') || key.includes('supabase')) localStorage.removeItem(key);
              });
              Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith('sb-') || key.includes('supabase')) sessionStorage.removeItem(key);
              });
            } catch (storageError) {
              console.warn('⚠️ [登录] 清除本地存储失败:', storageError);
            }

            await supabase.auth.signOut();
            showMsg(t.emailNotConfirmed, 'error');
            setCodeSent(false);
            setVerificationCode('');
            setCodeSentTime(null);
            setCodeExpiresIn(null);
            return;
          }

          showMsg(t.loginOk, 'success');
          if (onSuccess) setTimeout(() => onSuccess(), 500);
        }
      } else {
        throw new Error(result.error || t.loginFail);
      }
    } catch (error: any) {
      const errorMsg = error.message || t.loginFail;
      showMsg(errorMsg, 'error');
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const accentGold = '#D7CDA1';
  const isDark = isDarkMode;
  const bgColor = isDark ? '#000000' : '#fafafa';
  const textPrimary = isDark ? '#FFFFFF' : '#111111';
  const textSecondary = isDark ? '#888888' : '#666666';
  const borderDark = isDark ? '#222222' : '#e5e5e5';
  const placeholderMuted = isDark ? '#444444' : '#94a3b8';
  const msgSuccessBg = isDark ? 'rgba(34, 80, 34, 0.5)' : '#ecfdf5';
  const msgSuccessBorder = isDark ? '#2d5a2d' : '#6ee7b7';
  const msgSuccessText = isDark ? '#86efac' : '#047857';
  const msgErrorBg = isDark ? 'rgba(80, 34, 34, 0.5)' : '#fef2f2';
  const msgErrorBorder = isDark ? '#5a2d2d' : '#fecaca';
  const msgErrorText = isDark ? '#fca5a5' : '#b91c1c';

  return (
    <div
      className="login-split-screen"
      style={{
        fontFamily: "'SF Mono', Monaco, Inconsolata, 'Roboto Mono', monospace",
        background: bgColor,
        color: textPrimary,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&family=Space+Grotesk:wght@400;500;700&family=JetBrains+Mono:wght@400;500&display=swap');
        .login-split-screen * { box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        .login-split-screen .split-main { flex: 1; display: grid; grid-template-columns: 1fr; min-height: 400px; }
        @media (min-width: 1024px) {
          .login-split-screen .split-main { grid-template-columns: minmax(0, calc(100% * 1.28 / 2.28 + 35px)) minmax(0, 1fr); gap: 0; min-height: 100%; }
          .login-split-screen .left-panel { padding: 0; position: relative; overflow: hidden; min-height: 0; }
          .login-split-screen .split-spacer { display: none; }
          .login-split-screen .right-panel { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 2rem 3rem; min-width: 0; }
        }
        .login-split-screen .left-panel { padding: 0; position: relative; overflow: hidden; min-height: 280px; border-bottom: none; }
        @media (max-width: 1023px) {
          .login-split-screen .right-panel { padding: 2rem 1.5rem; }
        }
        .login-split-screen .login-carousel-wrap { position: absolute; inset: 0; background: ${isDark ? '#0a0a0a' : '#e4e4e7'}; }
        .login-split-screen .login-carousel-slide { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: opacity 0.5s ease; pointer-events: none; }
        .login-split-screen .login-carousel-scrim { position: absolute; inset: 0; pointer-events: none; background: ${
          isDark
            ? 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.35) 100%)'
            : 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(250,250,250,0.5) 100%)'
        }; }
        .login-split-screen .form-container { width: min(100%, 518px); margin-left: auto; margin-right: auto; animation: loginFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards; opacity: 0; }
        @keyframes loginFadeIn { to { opacity: 1; } }
        .login-split-screen .form-header { margin-bottom: 2rem; }
        .login-split-screen .form-title { font-size: 2.73rem; font-weight: 500; letter-spacing: -0.02em; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 0.75rem; }
        .login-split-screen .form-title-en { font-family: 'Space Grotesk', sans-serif; letter-spacing: 0.05em; font-size: 1em; }
        .login-split-screen .form-desc { font-size: 0.875rem; color: ${textSecondary}; line-height: 1.5; }
        .login-split-screen .input-group { margin-bottom: 1.5rem; position: relative; }
        .login-split-screen .input-label { display: flex; justify-content: space-between; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.05em; margin-bottom: 0.5rem; color: ${textSecondary}; text-transform: uppercase; }
        .login-split-screen .input-label.cjk-label { text-transform: none; letter-spacing: 0.02em; }
        .login-split-screen .tech-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid ${borderDark}; color: ${textPrimary}; font-size: 1rem; padding: 0.75rem 0; font-family: inherit; transition: border-color 0.3s ease; border-radius: 0; }
        .login-split-screen .tech-input:focus { outline: none; border-bottom-color: ${accentGold}; }
        .login-split-screen .tech-input::placeholder { color: ${placeholderMuted}; }
        .login-split-screen .tech-input:disabled { opacity: 0.6; cursor: not-allowed; }
        .login-split-screen .submit-btn { width: 100%; background-color: ${accentGold}; color: ${isDark ? bgColor : '#111111'}; border: none; padding: 1.25rem; font-family: 'Space Grotesk', sans-serif; font-size: 1.66rem; font-weight: 500; letter-spacing: 0.05em; text-transform: uppercase; cursor: pointer; display: flex; justify-content: space-between; align-items: center; transition: all 0.3s ease; margin-top: 2rem; border-radius: 0.375rem; }
        .login-split-screen .submit-btn.cjk-btn { text-transform: none; }
        .login-split-screen .submit-btn:hover:not(:disabled) { background-color: ${textPrimary}; color: ${isDark ? bgColor : '#ffffff'}; }
        .login-split-screen .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .login-split-screen .submit-btn .arrow { transition: transform 0.3s ease; }
        .login-split-screen .submit-btn:hover:not(:disabled) .arrow { transform: translateX(5px); }
        .login-split-screen .form-footer { margin-top: 1.5rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; font-size: 0.875rem; border-top: 1px solid ${borderDark}; padding-top: 1.25rem; flex-wrap: wrap; }
        .login-split-screen .sign-up-link { color: ${textPrimary}; text-decoration: none; font-weight: 500; display: inline-flex; align-items: center; gap: 0.5rem; transition: color 0.3s ease; cursor: pointer; background: none; border: none; font-size: inherit; font-family: inherit; }
        .login-split-screen .sign-up-link:hover { color: ${accentGold}; }
        .login-split-screen .sign-up-link .en { font-family: 'Space Grotesk', sans-serif; font-size: 1.66rem; font-weight: 500; letter-spacing: 0.05em; }
        .login-split-screen .form-footer-hint { font-size: 0.875rem; color: ${textSecondary}; white-space: pre-line; max-width: 20em; line-height: 1.5; flex: 1; min-width: 12rem; }
        .login-split-screen .msg-box { margin-top: 1rem; padding: 0.75rem; font-size: 0.875rem; border: 1px solid; white-space: pre-line; }
        .login-split-screen .msg-box.success { background: ${msgSuccessBg}; border-color: ${msgSuccessBorder}; color: ${msgSuccessText}; }
        .login-split-screen .msg-box.error { background: ${msgErrorBg}; border-color: ${msgErrorBorder}; color: ${msgErrorText}; }
        .login-split-screen .pwd-toggle { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; color: ${textSecondary}; cursor: pointer; padding: 0.25rem; }
        .login-split-screen .pwd-toggle:hover { color: ${textPrimary}; }
        .login-split-screen .forgot-password-row { text-align: right; margin-top: 0.35rem; margin-bottom: 0.15rem; }
        .login-split-screen .forgot-password-link { background: none; border: none; padding: 0; font-size: 0.8125rem; color: ${textSecondary}; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; font-family: inherit; }
        .login-split-screen .forgot-password-link:hover { color: ${accentGold}; }
        .login-split-screen .meta-line { font-size: 0.75rem; color: ${textSecondary}; margin-top: 0.35rem; }
      `}</style>

      <main className="split-main">
        <section className="left-panel" aria-label="Gallery">
          <div className="login-carousel-wrap">
            {LOGIN_CAROUSEL_SRC.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className="login-carousel-slide"
                style={{ opacity: i === carouselIdx ? 1 : 0 }}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            ))}
            <div className="login-carousel-scrim" aria-hidden />
          </div>
        </section>
        <div className="split-spacer" aria-hidden />
        <section className="right-panel">
          <div className="form-container">
            <div className="form-header">
              <h2 className="form-title">
                <span className="form-title-en">{t.formTitle}</span>
              </h2>
              <p className="form-desc">{codeSent ? t.descAfterCode : t.descBeforeCode}</p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="input-group">
                <div className={`input-label ${cjkHero ? 'cjk-label' : ''}`}>
                  <div>{t.labelEmail}</div>
                  <span style={{ color: accentGold }}>*</span>
                </div>
                <input
                  type="email"
                  className="tech-input"
                  placeholder={t.phEmail}
                  required
                  disabled={codeSent}
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="input-group">
                <div className={`input-label ${cjkHero ? 'cjk-label' : ''}`}>
                  <div>{t.labelPassword}</div>
                  <span style={{ color: accentGold }}>*</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="tech-input"
                    placeholder={t.phPassword}
                    required
                    minLength={6}
                    disabled={codeSent}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {!codeSent && (
                    <button
                      type="button"
                      className="pwd-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t.hidePwd : t.showPwd}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {!codeSent && onForgotPassword && (
                <div className="forgot-password-row">
                  <button type="button" className="forgot-password-link" onClick={onForgotPassword}>
                    {t.forgotPassword}
                  </button>
                </div>
              )}

              {codeSent && (
                <div className="input-group">
                  <div className={`input-label ${cjkHero ? 'cjk-label' : ''}`}>
                    <div>{t.labelCode}</div>
                    <span style={{ color: accentGold }}>*</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="tech-input"
                    placeholder={t.phCode}
                    value={verificationCode}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 6) value = value.substring(0, 6);
                      setVerificationCode(value);
                    }}
                    onInput={(e) => {
                      const input = e.target as HTMLInputElement;
                      if (input.value.length > 6) input.value = input.value.substring(0, 6);
                    }}
                    required={codeSent}
                    autoComplete="off"
                    maxLength={6}
                  />
                  <div className="meta-line">
                    {countdown > 0 && <div>{t.resendIn(countdown)}</div>}
                    {codeExpiresIn !== null && codeExpiresIn > 0 && (
                      <div className={codeExpiresIn <= 60 ? (isDark ? 'text-red-400' : 'text-red-600') : ''}>
                        {t.expiresIn(Math.floor(codeExpiresIn / 60), codeExpiresIn % 60)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {message ? (
                <div className={`msg-box ${messageTone === 'success' ? 'success' : 'error'}`}>{message}</div>
              ) : null}

              <button
                type="submit"
                className={`submit-btn ${cjkHero ? 'cjk-btn' : ''}`}
                disabled={loading || sendingCode || (codeSent && !verificationCode)}
              >
                <span>
                  {loading ? (codeSent ? t.btnVerifying : t.btnSending) : codeSent ? t.btnVerify : t.btnSendCode}
                </span>
                <span className="arrow">→</span>
              </button>
            </form>

            {onSwitchToSignUp && (
              <div className="form-footer">
                <div className="form-footer-hint">{t.footerHint}</div>
                <button type="button" className="sign-up-link" onClick={onSwitchToSignUp}>
                  <span className="en">{t.signUpCta}</span> →
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Login;
